// Invoice generation. The PDF itself is rendered SERVER-SIDE (a Cloud Function)
// so it's byte-identical on web, iOS, and Android and is filed in the horse's
// Documents by the server. This file builds the invoice HTML the server renders
// and handles the platform-specific "now show/send it" step afterward.

import { Platform } from 'react-native';
import { callGenerateInvoice } from '../firebase/functions';

let Sharing = null;
let FileSystem = null;
try {
  Sharing = require('expo-sharing');
  FileSystem = require('expo-file-system/legacy');
} catch {
  // Native share/download modules missing (web) — handled per-platform below.
}

// Server-side generation works everywhere, so the feature is always available.
export const isInvoiceAvailable = () => true;

const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const prettyDate = (ymd) => {
  if (!ymd) return '';
  const d = new Date(ymd + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// A statement-style invoice of OUTSTANDING charges. Each charge is itemized as
// "new" exactly once — on the invoice that first bills it — and thereafter is
// shown in a "Previously invoiced" section tagged with that invoice's number, so
// nothing is ever double-billed.
//   previously: [{ date, description, amount(=remaining), invoiceNumber }]
//   current:    [{ date, description, amount(=remaining) }]  (billed new here)
//   credit:     unapplied prepayment held on the account (reduces balance due)
// opts: { business, ownerContact, horse, invoiceNumber, notes }
// `groups` bills several horses on one invoice: [{ horse, previously, current }].
// Omit it and the single-horse invoice renders exactly as it always has — one
// horse is simply a single group.
export const buildInvoiceHtml = ({ previously = [], current = [], credit = 0, business = {}, ownerContact = {}, horse = {}, groups = null, invoiceNumber = '', notes = '' }) => {
  const bySum = (list) => list.reduce((s, e) => s + (e.amount || 0), 0);
  const gs = (groups && groups.length) ? groups : [{ horse, previously, current }];
  const multi = gs.length > 1;
  const allPrev = gs.flatMap((g) => g.previously || []);
  const allCurrent = gs.flatMap((g) => g.current || []);
  const prevSubtotal = bySum(allPrev);
  const newSubtotal = bySum(allCurrent);
  const creditAmt = Math.max(0, credit || 0);
  const balance = prevSubtotal + newSubtotal - creditAmt;

  const byDate = (a, b) => (a.date || '').localeCompare(b.date || '');
  const chargeRow = (e, showInv) => `<tr>
      <td class="d">${esc(prettyDate(e.date))}</td>
      <td class="desc">${esc(e.description || 'Charge')}${showInv && e.invoiceNumber ? `<span class="tag">${esc(e.invoiceNumber)}</span>` : ''}${e.note ? `<div class="cnote">${esc(e.note).split('\n').join('<br />')}</div>` : ''}</td>
      <td class="amt">${money(e.amount)}</td>
    </tr>`;

  const section = (title, list, showInv, subLabel) => {
    if (!list.length) return '';
    return `<tr><td colspan="3" class="mhead">${esc(title)}</td></tr>`
      + list.slice().sort(byDate).map((e) => chargeRow(e, showInv)).join('')
      + `<tr class="msub"><td></td><td class="amt-lbl">${esc(subLabel)}</td><td class="amt">${money(bySum(list))}</td></tr>`;
  };

  // One horse's worth of rows: carry-over split out when there is any.
  const groupRows = (g) => {
    const prev = g.previously || [];
    const cur = g.current || [];
    if (prev.length) {
      return section('Previously invoiced', prev, true, 'Subtotal — previously invoiced')
        + section('New charges', cur, false, 'Subtotal — new charges');
    }
    return cur.slice().sort(byDate).map((e) => chargeRow(e, false)).join('');
  };

  const horseLabel = (h) => esc((h && (h.name || h.registeredName)) || 'Horse');

  let rows;
  if (multi) {
    // Each horse gets its own heading and its own total, so an owner can see
    // what each horse cost without unpicking one long list.
    rows = gs.map((g) => {
      const gross = bySum(g.previously || []) + bySum(g.current || []);
      // A horse's prepayment credit is its own — applied in its section, so the
      // per-horse totals still add up to the balance due at the bottom.
      const cr = Math.max(0, g.credit || 0);
      return `<tr><td colspan="3" class="hhead">${horseLabel(g.horse)}</td></tr>`
        + groupRows(g)
        + (cr > 0.005 ? `<tr class="mcred"><td></td><td class="amt-lbl">Less prepayment credit</td><td class="amt">&minus;${money(cr)}</td></tr>` : '')
        + `<tr class="hsub"><td></td><td class="amt-lbl">Total — ${horseLabel(g.horse)}</td><td class="amt">${money(gross - cr)}</td></tr>`;
    }).join('');
  } else {
    rows = groupRows(gs[0]);
  }

  const horseName = multi
    ? `${gs.length} horses`
    : esc(gs[0].horse?.name || gs[0].horse?.registeredName || 'Horse');
  const horseSub = multi
    ? `<div class="sub">${gs.map((g) => horseLabel(g.horse)).join(', ')}</div>`
    : (gs[0].horse?.registeredName && gs[0].horse?.name ? `<div class="sub">${esc(gs[0].horse.registeredName)}</div>` : '');
  const bizContact = [business.phone, business.email].filter(Boolean).map(esc).join(' &middot; ');
  const ownerLines = [ownerContact.email, ownerContact.phone].filter(Boolean).map((x) => `<div class="sub">${esc(x)}</div>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, Helvetica, Arial, sans-serif; color: #1A1A1A; margin: 0; padding: 0; font-size: 13px; }
    .head { background: #4A0E0E; color: #F5EDD8; padding: 20px 24px; border-radius: 10px; display: flex; justify-content: space-between; align-items: flex-start; }
    .biz { font-size: 18px; font-weight: 600; }
    .biz-c { color: #D4AF37; font-size: 12px; margin-top: 3px; }
    .biz-a { color: #C9A86A; font-size: 11px; margin-top: 1px; }
    .inv { text-align: right; }
    .inv .t { font-size: 20px; font-weight: 600; letter-spacing: 1px; }
    .inv .n { color: #D4AF37; font-size: 12px; margin-top: 3px; }
    .meta { display: flex; justify-content: space-between; padding: 18px 4px 14px; border-bottom: 1px solid #EAE4DA; }
    .lbl { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; }
    .val { font-size: 14px; font-weight: 600; margin-top: 3px; }
    .sub { font-size: 12px; color: #666; }
    .right { text-align: right; }
    table { width: 100%; border-collapse: collapse; margin-top: 6px; }
    th { text-align: left; font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px 0 8px; }
    th.amt, td.amt { text-align: right; }
    td { padding: 9px 0; border-top: 1px solid #EFEBE3; }
    td.d { color: #666; width: 84px; }
    td.desc.pay, td.amt.pay { color: #3B6D11; }
    td.mhead { padding: 16px 0 4px; font-weight: 600; color: #4A0E0E; border-top: none; font-size: 13px; }
    tr.msub td { border-top: none; padding: 2px 0 8px; border-bottom: 1px solid #EAE4DA; }
    td.amt-lbl { text-align: right; color: #999; font-size: 11px; }
    tr.msub td.amt { font-weight: 600; }
    .cnote { color: #6B6B6B; font-size: 11.5px; line-height: 1.45; margin-top: 2px; white-space: pre-wrap; }
    td.hhead { background: #4A0E0E; color: #F5EDD8; font-weight: 700; padding: 7px 10px; font-size: 13px; border-top: none; }
    tr.hsub td { border-top: 2px solid #4A0E0E; border-bottom: none; font-weight: 700; padding: 6px 0 14px; }
    tr.mcred td { border-top: none; border-bottom: none; padding: 2px 0; color: #3B6D11; }
    tr.mcred td.amt { font-weight: 600; color: #3B6D11; }
    .tag { display: inline-block; margin-left: 8px; padding: 1px 6px; background: #F1ECE0; color: #8A6D2F; border-radius: 4px; font-size: 10px; font-weight: 600; letter-spacing: 0.3px; vertical-align: middle; }
    .totals { margin-top: 14px; margin-left: auto; width: 260px; }
    .trow { display: flex; justify-content: space-between; padding: 4px 0; color: #555; }
    .bal { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding: 12px 16px; background: #4A0E0E; color: #F5EDD8; border-radius: 8px; }
    .bal .b { font-size: 18px; font-weight: 600; }
    .notes { margin-top: 26px; padding-top: 12px; border-top: 1px solid #EAE4DA; color: #555; font-size: 12px; white-space: pre-wrap; }
    .paybox { margin-top: 22px; padding: 14px 16px; border: 1px solid #EAE4DA; border-radius: 10px; background: #FBF8F3; }
    .paybtn { display: inline-block; background: #4A0E0E; color: #FFFFFF !important; text-decoration: none; font-weight: 700; font-size: 14px; padding: 9px 18px; border-radius: 8px; }
    .paytext { font-size: 12.5px; color: #333; margin-top: 8px; }
    .paynote { color: #757575; font-size: 11px; margin-top: 4px; }
    .foot { margin-top: 28px; text-align: center; color: #B0A99C; font-size: 11px; }
  </style></head>
  <body>
    <div class="head">
      <div style="display:flex; align-items:center; gap:14px;">
        ${business.logoUrl ? `<img src="${esc(business.logoUrl)}" alt="" style="width:56px; height:56px; border-radius:8px; object-fit:cover; background:#fff;" />` : ''}
        <div>
          <div class="biz">${esc(business.name || 'Your Business')}</div>
          ${bizContact ? `<div class="biz-c">${bizContact}</div>` : ''}
          ${business.address ? `<div class="biz-a">${esc(business.address)}</div>` : ''}
        </div>
      </div>
      <div class="inv">
        <div class="t">INVOICE</div>
        ${invoiceNumber ? `<div class="n">#${esc(invoiceNumber)}</div>` : ''}
      </div>
    </div>

    <div class="meta">
      <div>
        <div class="lbl">Bill to</div>
        <div class="val">${esc(ownerContact.name || 'Owner')}</div>
        ${ownerLines}
      </div>
      <div class="right">
        <div class="lbl">${multi ? 'Horses' : 'Horse'}</div>
        <div class="val">${horseName}</div>
        ${horseSub}
        <div class="sub" style="margin-top:6px">Issued ${esc(prettyDate(new Date().toISOString().split('T')[0]))}</div>
      </div>
    </div>

    <table>
      <thead><tr><th>Date</th><th>Description</th><th class="amt">Amount</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="3" style="color:#999;padding:16px 0">No outstanding charges.</td></tr>'}</tbody>
    </table>

    <div class="totals">
      ${allPrev.length ? `<div class="trow"><span>Previously invoiced</span><span>${money(prevSubtotal)}</span></div>` : ''}
      ${allPrev.length ? `<div class="trow"><span>New charges</span><span>${money(newSubtotal)}</span></div>` : ''}
      ${creditAmt > 0.005 ? `<div class="trow"><span>Less prepayment credit</span><span>&minus;${money(creditAmt)}</span></div>` : ''}
      <div class="bal"><span>Balance due</span><span class="b">${money(balance)}</span></div>
    </div>

    <!--HM_PAY_LINK-->
    ${notes ? `<div class="notes">${esc(notes)}</div>` : ''}
    <div class="foot">Generated with Horse Math</div>
  </body></html>`;
};

// An account STATEMENT: every debit (charge, +) and credit (payment, −) in a
// date range, in chronological order, with opening / running / closing balance.
// Unlike an invoice, it lists payments — it's the reconciliation/accounting doc.
//   rows:    the ledger entries (charge|payment) already filtered to the range
//   opening: net owed BEFORE the range (carried in as the starting balance)
// opts: { business, ownerContact, horse, periodLabel, opening, notes }
export const buildStatementHtml = ({ rows = [], opening = 0, business = {}, ownerContact = {}, horse = {}, periodLabel = '', notes = '' }) => {
  const sorted = rows.slice().sort((a, b) => (a.date || '').localeCompare(b.date || '') || ((a.type === 'payment') - (b.type === 'payment')));
  let running = opening || 0;
  const charges = sorted.filter((e) => e.type === 'charge').reduce((s, e) => s + (e.amount || 0), 0);
  const payments = sorted.filter((e) => e.type === 'payment').reduce((s, e) => s + (e.amount || 0), 0);
  const closing = (opening || 0) + charges - payments;

  const bodyRows = sorted.map((e) => {
    const isPay = e.type === 'payment';
    running += isPay ? -(e.amount || 0) : (e.amount || 0);
    return `<tr>
      <td class="d">${esc(prettyDate(e.date))}</td>
      <td class="desc">${esc(e.description || (isPay ? 'Payment received' : 'Charge'))}</td>
      <td class="amt">${isPay ? '' : money(e.amount)}</td>
      <td class="amt pay">${isPay ? money(e.amount) : ''}</td>
      <td class="amt bal">${money(running)}</td>
    </tr>`;
  }).join('');

  const horseName = esc(horse.name || horse.registeredName || 'Horse');
  const horseSub = horse.registeredName && horse.name ? `<div class="sub">${esc(horse.registeredName)}</div>` : '';
  const bizContact = [business.phone, business.email].filter(Boolean).map(esc).join(' &middot; ');
  const ownerLines = [ownerContact.email, ownerContact.phone].filter(Boolean).map((x) => `<div class="sub">${esc(x)}</div>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, Helvetica, Arial, sans-serif; color: #1A1A1A; margin: 0; padding: 0; font-size: 13px; }
    .head { background: #4A0E0E; color: #F5EDD8; padding: 20px 24px; border-radius: 10px; display: flex; justify-content: space-between; align-items: flex-start; }
    .biz { font-size: 18px; font-weight: 600; }
    .biz-c { color: #D4AF37; font-size: 12px; margin-top: 3px; }
    .biz-a { color: #C9A86A; font-size: 11px; margin-top: 1px; }
    .inv { text-align: right; }
    .inv .t { font-size: 20px; font-weight: 600; letter-spacing: 1px; }
    .inv .n { color: #D4AF37; font-size: 12px; margin-top: 3px; }
    .meta { display: flex; justify-content: space-between; padding: 18px 4px 14px; border-bottom: 1px solid #EAE4DA; }
    .lbl { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; }
    .val { font-size: 14px; font-weight: 600; margin-top: 3px; }
    .sub { font-size: 12px; color: #666; }
    .right { text-align: right; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th { text-align: left; font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px 0 8px; }
    th.amt, td.amt { text-align: right; }
    td { padding: 8px 0; border-top: 1px solid #EFEBE3; }
    td.d { color: #666; width: 78px; }
    td.amt.pay { color: #3B6D11; }
    td.amt.bal { font-weight: 600; width: 92px; }
    tr.open td { border-top: none; color: #666; font-style: italic; }
    .totals { margin-top: 14px; margin-left: auto; width: 280px; }
    .trow { display: flex; justify-content: space-between; padding: 4px 0; color: #555; }
    .bal-box { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding: 12px 16px; background: #4A0E0E; color: #F5EDD8; border-radius: 8px; }
    .bal-box .b { font-size: 18px; font-weight: 600; }
    .notes { margin-top: 26px; padding-top: 12px; border-top: 1px solid #EAE4DA; color: #555; font-size: 12px; white-space: pre-wrap; }
    .foot { margin-top: 28px; text-align: center; color: #B0A99C; font-size: 11px; }
  </style></head>
  <body>
    <div class="head">
      <div style="display:flex; align-items:center; gap:14px;">
        ${business.logoUrl ? `<img src="${esc(business.logoUrl)}" alt="" style="width:56px; height:56px; border-radius:8px; object-fit:cover; background:#fff;" />` : ''}
        <div>
          <div class="biz">${esc(business.name || 'Your Business')}</div>
          ${bizContact ? `<div class="biz-c">${bizContact}</div>` : ''}
          ${business.address ? `<div class="biz-a">${esc(business.address)}</div>` : ''}
        </div>
      </div>
      <div class="inv">
        <div class="t">STATEMENT</div>
        ${periodLabel ? `<div class="n">${esc(periodLabel)}</div>` : ''}
      </div>
    </div>

    <div class="meta">
      <div>
        <div class="lbl">Account</div>
        <div class="val">${esc(ownerContact.name || 'Owner')}</div>
        ${ownerLines}
      </div>
      <div class="right">
        <div class="lbl">Horse</div>
        <div class="val">${horseName}</div>
        ${horseSub}
        <div class="sub" style="margin-top:6px">Issued ${esc(prettyDate(new Date().toISOString().split('T')[0]))}</div>
      </div>
    </div>

    <table>
      <thead><tr><th>Date</th><th>Description</th><th class="amt">Charges</th><th class="amt">Payments</th><th class="amt">Balance</th></tr></thead>
      <tbody>
        <tr class="open"><td></td><td>Opening balance</td><td></td><td></td><td class="amt bal">${money(opening || 0)}</td></tr>
        ${bodyRows || '<tr><td colspan="5" style="color:#999;padding:16px 0">No activity in this period.</td></tr>'}
      </tbody>
    </table>

    <div class="totals">
      <div class="trow"><span>Opening balance</span><span>${money(opening || 0)}</span></div>
      <div class="trow"><span>Charges this period</span><span>${money(charges)}</span></div>
      <div class="trow"><span>Payments this period</span><span>&minus;${money(payments)}</span></div>
      <div class="bal-box"><span>Closing balance</span><span class="b">${money(closing)}</span></div>
    </div>

    ${notes ? `<div class="notes">${esc(notes)}</div>` : ''}
    <div class="foot">Generated with Horse Math</div>
  </body></html>`;
};

// Render a server-side PDF (invoice or statement), which files it in the horse's
// Documents, then present it. Resolves to 'opened' | 'shared' | 'saved' AS SOON
// AS the PDF is saved — the share sheet / new tab is presented without blocking,
// because iOS Sharing.shareAsync can hang until dismissed and must never freeze
// the UI.
const generateAndPresent = async (horseId, html, fileName, docType, dialogTitle, horseIds = null, pay = null) => {
  const res = await callGenerateInvoice(horseId, html, fileName, docType, horseIds, pay); // <-- doc is saved here
  const { url } = res;
  // Emailed to the client: nothing to present. If the email failed, say so
  // (the PDF is saved either way) instead of opening a share sheet.
  if (pay && pay.emailTo) return res.emailed ? 'emailed' : `email_failed:${res.emailError || ''}`;
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') window.open(url, '_blank');
    return 'opened';
  }
  // Native: download + share as a detached task so the caller can close the
  // modal and stop its spinner immediately, regardless of the share sheet.
  if (FileSystem && Sharing) {
    (async () => {
      try {
        const target = FileSystem.cacheDirectory + fileName.replace(/[^\w.\- ]+/g, '');
        const { uri } = await FileSystem.downloadAsync(url, target);
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle, UTI: 'com.adobe.pdf' });
        }
      } catch {
        // The PDF is already saved to Documents; sharing is best-effort.
      }
    })();
    return 'shared';
  }
  return 'saved';
};

export const generateAndShareInvoice = (params) =>
  generateAndPresent(
    params.horseId,
    buildInvoiceHtml(params),
    params.fileName || 'Invoice.pdf',
    'Invoice',
    'Send invoice',
    params.horseIds || null,
    {
      invoiceNumber: params.invoiceNumber || '',
      emailTo: params.emailTo || '',
      emailTotal: params.emailTotal || 0,
      clientName: (params.ownerContact && params.ownerContact.name) || '',
    },
  );

export const generateAndShareStatement = (params) =>
  generateAndPresent(params.horseId, buildStatementHtml(params), params.fileName || 'Statement.pdf', 'Statement', 'Send statement');
